import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DialogActions, DialogShell } from './ConfirmDialog';
import type { Mp4Preset, VideoMp4s } from '../types/video';

interface Mp4DownloadDialogProps {
  /** MP4s already made (download at once). */
  ready: VideoMp4s;
  /** The video already is an MP4: Original is its own file. */
  isMp4?: boolean;
  onDownload: (preset: Mp4Preset) => void;
  onCancel: () => void;
}

const formatSize = (bytes: number) =>
  bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(1)} GB` : `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;

/** Choose the MP4 quality: original size, 1080p or 720p. */
export default function Mp4DownloadDialog({ ready, isMp4, onDownload, onCancel }: Mp4DownloadDialogProps) {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<Mp4Preset>('original');

  const options: { id: Mp4Preset; label: string; hint: string }[] = [
    {
      id: 'original',
      label: t('mp4.preset_original', 'Original'),
      hint: isMp4
        ? t('mp4.preset_original_own', 'This video is already MP4 — downloads the file itself')
        : t('mp4.preset_original_hint', 'Same size as the video — sharpest'),
    },
    { id: '1080', label: '1080p', hint: t('mp4.preset_1080_hint', 'Full HD — good for most screens') },
    { id: '720', label: '720p', hint: t('mp4.preset_720_hint', 'Smaller file — quick to share') },
  ];

  return (
    <DialogShell title={t('mp4.dialog_title', 'Download as MP4')} onCancel={onCancel}>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
        {t('mp4.dialog_hint', 'MP4 plays everywhere. Each quality is made once, kept for later downloads, and counts toward your storage.')}
      </p>
      <div role="radiogroup" aria-label={t('mp4.quality', 'Quality')} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {options.map((o) => {
          const selected = preset === o.id;
          const made = ready[o.id];
          return (
            <label
              key={o.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                border: `1px solid ${selected ? 'var(--theme-primary, #1976d2)' : 'var(--theme-border, #e5e7eb)'}`,
                background: selected ? 'color-mix(in srgb, var(--theme-primary, #1976d2) 8%, transparent)' : 'transparent',
              }}
            >
              <input type="radio" name="mp4-preset" checked={selected} onChange={() => setPreset(o.id)} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{o.label}</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>{o.hint}</span>
              </span>
              {made && (
                <span
                  title={t('mp4.ready_hint', 'Already made — downloads at once')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--theme-success, #16a34a)', whiteSpace: 'nowrap' }}
                >
                  <CheckCircle2 size={14} aria-hidden />
                  {t('mp4.ready_badge', 'Ready')} · {formatSize(made.size)}
                </span>
              )}
            </label>
          );
        })}
      </div>
      <DialogActions
        confirmLabel={ready[preset] ? t('mp4.download_ready', 'Download MP4') : t('mp4.convert_download', 'Convert and download')}
        cancelLabel={t('cancel', 'Cancel')}
        onConfirm={() => onDownload(preset)}
        onCancel={onCancel}
      />
    </DialogShell>
  );
}
